import React, { Suspense, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { useSelector } from 'react-redux';

import { RouteEnum } from 'app/shared/enums';
import { warmupHomepageConnection } from 'app/shared/helpers/warmupHomepageConnection';

import { TravelCalculationBaseViewProps } from './TravelCalculationBaseView.interface';
import { useTravelCalculationBaseView } from './useTravelCalculationBaseView';
import { CalculationViewContentPlaceholder } from 'app/components/CalculationViewContentPlaceholder';
import { TravelCalculationViewPageHeader } from 'app/components/TravelCalculationViewPageHeader';
import TravelQuestionsAndAnswers from 'app/components/TravelQuestionsAndAnswers';
import lazyWithReload from 'app/shared/helpers/lazyWithReload';
import retryImport from 'app/shared/helpers/retryImport';
import {
  isPrerendering,
  wasPrerendered,
} from 'app/shared/helpers/isPrerendering';
import { selectTravelQuotationStatus } from 'app/store/data/travelQuotation';
import { TravelCalculationViewContentPlaceholder } from 'app/components/TravelCalculationViewContentPlaceholder';

const TravelSummaryView = lazyWithReload(() =>
  retryImport(() => import('app/views/TravelSummaryView'))
);

const TravelCheckoutBaseView = lazyWithReload(() =>
  retryImport(() => import('app/views/TravelCheckoutBaseView'))
);

const TravelFormView = lazyWithReload(() =>
  retryImport(() => import('app/views/TravelFormView'))
);

const NotFoundPage = lazyWithReload(() =>
  retryImport(() => import('app/components/NotFoundPage'))
);

const TravelCalculationViewComponents = () => {
  const travelQuotationStatus = useSelector(selectTravelQuotationStatus);

  return (
    <>
      <TravelCalculationViewPageHeader />
      {travelQuotationStatus && !isPrerendering ? (
        <Suspense
          fallback={
            <TravelCalculationViewContentPlaceholder
              showLoadingAnimation={false}
            />
          }
        >
          <TravelFormView />
        </Suspense>
      ) : (
        <>
          <TravelCalculationViewContentPlaceholder
            showLoadingAnimation={false}
          />
          <div id="seo-prerender" />
        </>
      )}
      <TravelQuestionsAndAnswers />
    </>
  );
};

export default function TravelCalculationBaseView(
  props: TravelCalculationBaseViewProps
) {
  const {
    location,
    shouldRedirectToCalculationForm,
    shouldRedirectToResults,
    travelQuotationStatus,
  } = useTravelCalculationBaseView();

  useEffect(() => {
    warmupHomepageConnection();
  }, []);

  if (!wasPrerendered && !travelQuotationStatus) {
    return <CalculationViewContentPlaceholder />;
  }

  if (shouldRedirectToCalculationForm) {
    return <Redirect to={RouteEnum.TRAVEL_FORM} />;
  }

  if (shouldRedirectToResults) {
    return <Redirect to={RouteEnum.TRAVEL_CALCULATION_RESULTS} />;
  }

  return (
    <Suspense fallback={<div />}>
      <Switch location={location}>
        <Route
          exact
          path={RouteEnum.TRAVEL}
          component={TravelCalculationViewComponents}
        />
        <Route
          path={RouteEnum.TRAVEL_FORM}
          component={TravelCalculationViewComponents}
        />
        <Route
          path={RouteEnum.TRAVEL_FORM_FB}
          component={TravelCalculationViewComponents}
        />
        <Route
          path={RouteEnum.TRAVEL_CALCULATION_RESULTS}
          component={TravelSummaryView}
        />
        <Route
          path={`${RouteEnum.TRAVEL_FORM_CHECKOUT}/:offerId`}
          component={TravelCheckoutBaseView}
        />
        <Route component={NotFoundPage} />
      </Switch>
    </Suspense>
  );
}
